import { useCallback, useEffect, useRef, useState } from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useIntl } from 'react-intl';
import { v4 as createUUID } from 'uuid';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { getConsents } from '@/components/signup/utils/get-consents';
import { getSignupContext } from '@/components/signup/utils/get-signup-context';
import { isTestTenantByName } from '@/components/signup/utils/is-test-tenant-by-name';
import {
	Edition,
	ProductKey,
	mapProductKeyAndEditionToCcpProduct,
} from '@/components/signup/utils/products';
import { getRecaptcha } from '@/components/signup/utils/recaptcha';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import type { ActivateProductResponse } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

/* -------------------------------------------------------------------------- */
/*                                Public types                                */
/* -------------------------------------------------------------------------- */

export type DeferSiteActivationStatus = 'idle' | 'loading' | 'success' | 'error';

export type DeferSiteActivationResult = {
	/** The site name the server chose for this user. */
	readonly recommendedName: string;
	/** The full ActivateProduct response — same shape used by the existing
	 * non-deferred flow. Consumers can derive provisioningRequestId,
	 * orchestrationId, etc. from it. */
	readonly response: ActivateProductResponse;
	/** A fresh signupId generated at the start of the activation. Re-used by
	 * consumers (e.g. CloudStatusProvider) for analytics stitching across the
	 * deferred-activation and provisioning phases. */
	readonly signupId: string;
};

export type DeferSiteActivationState = {
	readonly status: DeferSiteActivationStatus;
	readonly result?: DeferSiteActivationResult;
	readonly error?: Error;
};

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

/**
 * Core orchestration hook for the Defer Site Naming experiment (D1D6-47).
 *
 * When a user lands on the confirmation page with `?deferSiteNaming=true` (and
 * the experiment variant arm), this hook runs the activation sequence the
 * sign-up form would normally have run *before* navigating to the confirmation
 * page:
 *
 *   1. `getRecommendedSiteName({ email })` to get a server-chosen site name.
 *   2. `getConsentConfig()` + `getConsents()` mirroring `SiteActivationContextProvider`.
 *   3. `activateProduct({ ... })` to actually create the site.
 *
 * On success, the hook exposes the activation response (cloudId,
 * provisioningRequestId, orchestrationId, signupId, recommendedName) in
 * local state. The consumer (`DeferSiteNamingGate` in D1D6-48) calls
 * `window.history.replaceState` with the merged URL so `useSearchParameters`
 * picks up the post-handoff params, then renders children — at which point
 * the existing `CloudStatusProvider` / polling chain takes over. We
 * intentionally use `replaceState` rather than `window.location.assign` so
 * the handoff stays a client-side state transition (no full page reload,
 * no React tree remount).
 *
 * Scope:
 *
 *   - N2N (new-to-new) and Jira-only by design. Skips the site-picker logic in
 *     `SiteActivationContextProvider` entirely.
 *   - reCAPTCHA is already provided by `SmartReCaptcha` in
 *     `page-wrapper-streaming.tsx`; no provider setup is required here.
 *
 * Reference: Technical Design §6.2 + §6.4 + §6.7
 *   https://hello.atlassian.net/wiki/spaces/Land/pages/6991285952
 */
export function useDeferSiteActivation(): DeferSiteActivationState {
	/* -------------------------------- context -------------------------------- */
	const { deferSiteNaming, onDemandUrl, products, edition, primaryCollectionProduct, platform } =
		useSearchParameters();
	const { user } = useUserContext();
	const stargateClient = useBxpWebStargateClient();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	/* --------------------------------- state --------------------------------- */
	// Idempotence guard for refresh-after-success (D1D6-48):
	// If the URL already has the activation result on it, treat the
	// deferred path as already-handed-off and start at "idle" so the gate
	// passes through to children. The effect below ALSO short-circuits, so
	// we never run activateProduct again. See the matching comment there.
	// `onDemandUrl` (i.e. `ondemandurl` in the URL) is the canonical
	// post-handoff signal — `buildHandoffSearchParams` always writes it
	// because `recommendedName` is required on success. We do NOT also
	// gate on `provisioningRequestId` because that param is CCP-only;
	// the COFS shape returns an `orchestrationId` instead and would
	// otherwise miss this guard, causing refresh-after-success on the
	// COFS path to create a duplicate site (caught in PR review).
	const alreadyHandedOff = deferSiteNaming && !!onDemandUrl;
	const [state, setState] = useState<DeferSiteActivationState>({
		status: deferSiteNaming && !alreadyHandedOff ? 'loading' : 'idle',
	});

	/* ----------------------------- guards / refs ----------------------------- */
	// React Strict Mode fires effects twice on mount in dev. activateProduct
	// is a write operation (creates a real site), so we MUST guard against
	// running the sequence more than once per mount.
	const hasStartedRef = useRef(false);
	// Flipped to true on unmount to suppress late state commits. Reset to
	// false on each mount so that React Strict Mode's mount → unmount → mount
	// cycle doesn't leave the ref permanently true (which would block the
	// second mount's effect from ever committing).
	const unmountedRef = useRef(false);
	useEffect(() => {
		unmountedRef.current = false;
		return () => {
			unmountedRef.current = true;
		};
	}, []);

	/* ----------------------------- analytics -------------------------------- */
	const fireTrackEvent = useCallback(
		(action: 'started' | 'succeeded' | 'failed', attributes: Record<string, unknown>) => {
			createAnalyticsEvent({
				eventType: 'track',
				action,
				actionSubject: 'deferSiteNaming',
				source: 'confirmationPage',
				attributes,
			}).fire();
		},
		[createAnalyticsEvent],
	);

	/* ----------------------------- effect ------------------------------------ */
	useEffect(() => {
		if (!deferSiteNaming) {
			return;
		}
		if (hasStartedRef.current) {
			return;
		}
		// Wait for reCAPTCHA to be ready before kicking off activation
		// (D1D6-48): the GoogleReCaptchaProvider takes ~1-2s to inject the
		// executeRecaptcha function after mount. If we run before it's
		// ready, getRecaptcha() returns undefined and we throw "missing
		// reCAPTCHA response" — the user sees a stuck "Finishing up"
		// animation because the gate falls through to the polling flow
		// with no activated site to poll against. Adding executeRecaptcha
		// to the dep array causes this effect to re-run when it flips
		// from undefined to the real function.
		if (!executeRecaptcha) {
			return;
		}
		// Idempotence guard for refresh-after-success (D1D6-48):
		// If the URL ALREADY has `onDemandUrl` on top of
		// `deferSiteNaming=true`, the user has likely refreshed the page
		// after a previous successful activation that wrote the params via
		// `history.replaceState` but didn't remove the flag (or the
		// rewrite raced with a subsequent route change). Re-running
		// activateProduct here would create a SECOND CCP order/site for
		// the same user, which is unrecoverable billing-side. Instead we
		// short-circuit straight to idle and let the existing polling
		// chain (which sees onDemandUrl in the URL) take over. The
		// DeferSiteNamingGate will not render the loading view since
		// `status === "idle"` falls through to children.
		//
		// `onDemandUrl` alone is the canonical post-handoff signal —
		// `buildHandoffSearchParams` sets it for BOTH CCP and COFS
		// responses, while `provisioningRequestId` is only set for CCP.
		// Guarding on `onDemandUrl && provisioningRequestId` would miss
		// COFS refreshes and create duplicate sites (see PR review
		// feedback on the original CCP-only guard).
		if (onDemandUrl) {
			// See `alreadyHandedOff` above — `onDemandUrl` alone is the
			// canonical post-handoff signal that works for both CCP and
			// COFS paths. Gating on `provisioningRequestId` here would
			// have leaked through COFS refreshes and created duplicate
			// sites.
			hasStartedRef.current = true;
			// No state set — leave `state.status` at "idle" so the gate
			// passes through to children.
			return;
		}
		hasStartedRef.current = true;

		const signupId = createUUID();
		const productKey = products[0] as ProductKey;
		const startedAt = Date.now();

		// Late-commit guard: if the consumer unmounts the hook mid-flight we
		// should not commit state updates. The unmountedRef is set only on
		// real unmount (not on Strict Mode's intermediate cleanup).
		const safeSetState = (next: DeferSiteActivationState) => {
			if (!unmountedRef.current) {
				setState(next);
			}
		};

		// useSearchParameters returns `["",]` (i.e. `"".split(",")`) when the
		// `products` URL param is missing, so a naked `products[0] as ProductKey`
		// would silently propagate an empty string into the activateProduct
		// payload and produce a generic 400 from CCP. The signup form is
		// expected to always include `products` on the deferred path, but we
		// fail fast here with a clear reason so the failure surface is
		// diagnosable rather than a server-side validation error.
		if (!productKey) {
			const error = new Error('defer-site-naming: missing or empty products URL parameter');
			fireTrackEvent('failed', {
				signupId,
				productKey,
				elapsedTimeMs: 0,
				failureReason: 'missing-products',
			});
			safeSetState({ status: 'error', error });
			return;
		}

		fireTrackEvent('started', {
			signupId,
			productKey,
		});

		(async () => {
			try {
				// ------ Step 1: recommended site name ------
				const { recommendedName } = await stargateClient.getRecommendedSiteName({
					email: user['email'] || '',
				});

				// ------ Step 2: consent + reCAPTCHA ------
				const [localeData, gRecaptchaResponse] = await Promise.all([
					stargateClient.getConsentConfig(),
					getRecaptcha(user['email'] || '', executeRecaptcha),
				]);

				if (!gRecaptchaResponse) {
					throw new Error('Defer site naming: missing reCAPTCHA response');
				}

				const consent = getConsents(
					localeData.locale ?? 'US-VA',
					localeData.localeRequiresMarketingCommunicationOptIn,
				);

				// ------ Step 3: activate the product ------
				const testingPurposes = isTestTenantByName(recommendedName);
				const signupContext = getSignupContext({
					productKey,
					testingPurposes,
					signupId,
					search: window.location.search,
					locale: intl.locale,
				});
				const MAX_SIGNUP_CONTEXT_LENGTH = 8192;
				let signupContextString: string = JSON.stringify(signupContext);
				if (signupContextString.length > MAX_SIGNUP_CONTEXT_LENGTH) {
					signupContextString = '{}';
				}
				// Default to "free" when no edition is in the URL — the
				// deferred Jira variant ships only the free edition for now,
				// matching the existing signup form default. CCP requires a
				// concrete edition; passing undefined is a type error.
				const resolvedEdition: Edition = (edition as Edition | undefined) ?? 'free';
				const ccpProducts = mapProductKeyAndEditionToCcpProduct({
					productKey,
					edition: resolvedEdition,
					primaryCollectionProduct: primaryCollectionProduct as ProductKey | undefined,
				});

				const response = await stargateClient.activateProduct({
					// Consistent with `email: user["email"] || ""` above —
					// CCP's activateProduct payload requires a string, and an
					// undefined displayName would cause a generic schema
					// validation failure rather than a graceful empty name.
					displayName: user['name'] || '',
					email: user['email'] || '',
					ccpProducts: [ccpProducts],
					siteName: recommendedName,
					timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					useCcpPlatform: platform === 'ccp',
					consent,
					platform,
					signupContext: signupContextString,
					// The deferred path is always N2N + Jira, so the user is
					// always new from this flow's perspective.
					isNewUser: true,
					gRecaptchaResponse,
					testingPurposes,
					signupId,
				});

				fireTrackEvent('succeeded', {
					signupId,
					productKey,
					elapsedTimeMs: Date.now() - startedAt,
				});

				safeSetState({
					status: 'success',
					result: { recommendedName, response, signupId },
				});
			} catch (caught) {
				const error = caught instanceof Error ? caught : new Error(String(caught));
				fireTrackEvent('failed', {
					signupId,
					productKey,
					elapsedTimeMs: Date.now() - startedAt,
					failureReason: error.message,
				});
				safeSetState({ status: 'error', error });
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
		// dep list. Two deps are listed because both can flip from a "not
		// ready" state to a "ready" state after first render:
		//   - `deferSiteNaming`: typically known at first render, but kept in
		//     deps because `useSearchParameters` could in theory update if the
		//     consumer ever rewrote the URL while this hook is mounted.
		//   - `executeRecaptcha`: starts out `undefined` and flips to the
		//     real `executeRecaptcha` function ~1-2s after
		//     `GoogleReCaptchaProvider` mounts. Activation cannot start
		//     before this flip (we'd throw "missing reCAPTCHA response"),
		//     so re-running the effect when it becomes available is
		//     required, not optional.
		// All other inputs are intentionally captured at first effect run
		// and are stable for the lifetime of the page (products, email,
		// cloudId, edition, etc). The `hasStartedRef` guard ensures the
		// body runs at most once even if either dep flips multiple times.
	}, [deferSiteNaming, executeRecaptcha]);

	return state;
}
